import { useMemo } from "react"
import { DateTime } from "luxon"
import { useField } from "formik"
import { Stack } from "@mui/material"

import { SelectedPriceListItem } from "../../../../../createOrderTypes"
import { CreateUpdateDeliveryFormValues } from "../../CreateUpdateDeliveryTimePopover"
import { DeliveryTimeDay } from "../../../../form/formTypes"

import { BookingDateMismatchMessage } from "./BookingDateMismatchMessage"
import { DeliveryDatePicker } from "./DeliveryDatePicker"
import { DeliveryTimesFieldArray } from "./DeliveryTimesFieldArray"
import { OpeningHoursHintMessage } from "./OpeningHoursHintMessage"
import { useDeliveryTimeLimits } from "./useDeliveryTimeLimits"

type DeliveryTimeFieldsProps = Readonly<{
    priceList: SelectedPriceListItem
    allowedWeekdays: number[]
    eventStartTime: DateTime | null
    closingDates: string[]
    openingHours?: DeliveryTimeDay[]
    maxAllowedDeliveries: number
    allowPastDates: boolean
    isDeliveryCountLocked?: boolean
}>

/** The delivery date + times inputs, shared by the delivery popover and the booking-move prompt. */
export function DeliveryTimeFields({
    priceList,
    allowedWeekdays,
    eventStartTime,
    closingDates,
    openingHours = [],
    maxAllowedDeliveries,
    allowPastDates,
    isDeliveryCountLocked,
}: DeliveryTimeFieldsProps) {
    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")

    const { limits, noSlotsForDate } = useDeliveryTimeLimits(deliveryDate, openingHours)

    // A meeting that starts outside the shop's delivery window does not prevent the order:
    // the user is told when the shop delivers and picks a time inside that window instead.
    const isEventStartOutsideDeliveryHours = useMemo(() => {
        if (!eventStartTime || !limits) return false

        const eventTimeOnDeliveryDate = limits.minTime.set({
            hour: eventStartTime.hour,
            minute: eventStartTime.minute,
            second: 0,
            millisecond: 0,
        })

        return eventTimeOnDeliveryDate < limits.minTime || eventTimeOnDeliveryDate > limits.maxTime
    }, [eventStartTime?.toISO(), limits?.minTime.toISO(), limits?.maxTime.toISO()])

    const isDeliveryOnAnotherDayThanBooking = useMemo(() => {
        if (!eventStartTime || !deliveryDate) return false

        return !deliveryDate.hasSame(eventStartTime, "day")
    }, [eventStartTime?.toISO(), deliveryDate?.toISO()])

    return (
        <Stack gap="1.25rem">
            <DeliveryDatePicker
                closingDates={closingDates}
                priceList={priceList}
                allowedWeekdays={allowedWeekdays}
                isDeliveryBlocked={noSlotsForDate}
                allowPastDates={allowPastDates}
            />

            {isDeliveryOnAnotherDayThanBooking && eventStartTime && deliveryDate ? (
                <BookingDateMismatchMessage bookingStart={eventStartTime} deliveryDate={deliveryDate} />
            ) : (
                // Only meaningful while both sit on the same day: comparing the meeting's
                // clock time against another day's opening hours says nothing useful, and
                // the mismatch message already carries the booking time.
                isEventStartOutsideDeliveryHours &&
                eventStartTime &&
                limits && <OpeningHoursHintMessage bookingStart={eventStartTime} openingLimits={limits} />
            )}

            <DeliveryTimesFieldArray
                openingHours={openingHours}
                isDeliveryBlocked={noSlotsForDate}
                maxAllowedDeliveries={maxAllowedDeliveries}
                isDeliveryCountLocked={isDeliveryCountLocked}
            />
        </Stack>
    )
}
